import React, { useEffect } from 'react';

import { Accordion, Button, Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { getUtcToLocal, isValueEmpty, toStringOrEmpty } from '@awp/helpers/utilsReact';
import { configStartDate } from '@awp/helpers/Utils';
import { EnabledFeature, STATE_STATUS, DEFAULT_PRESET_ID_FOR_ALL_DATASETS } from '@awp/constants';
import { IconExclamationCircleWarning } from '@components/awpIcons';
import AwpSpinner from '@components/Common/AwpSpinner';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import ForecastTimeDays from '@components/WeatherForecast/ForecastTimeDays';
import { useMultiStationContext } from '@components/Graphs/MultiStation/MultiStationContext';

import { useWeatherForecastContext, WeatherForecastContextProvider } from './WeatherForecastContext';
import {
  fetchWeatherForecast,
  fetchWeatherForecasts,
  selectDataRange,
  selectEndDate,
  selectStatus,
} from '@awp/stores/dataVisualizationSlice';
import ForecastContainer from '@components/WeatherForecast/ForecastContainer';
import WeatherForecastLegends from '@components/WeatherForecast/WeatherForecastLegends';

/**
 * WeatherForecast Component
 * Renders the Weather Forecast interface with context providers and container.
 *
 * @param {object} props - The props for WeatherForecast.
 * @returns {JSX.Element} - The rendered WeatherForecast component.
 */
const WeatherForecast = (props) => {
  return (
    <WeatherForecastContextProvider>
      <WeatherForecastComponent {...props} />
    </WeatherForecastContextProvider>
  );
};

/**
 * WeatherForecast Component
 * Component that renders the weather forecast details and returns them inside of the context provider
 *
 * @returns {JSX.Element|null} - The rendered component
 */
const WeatherForecastComponent = ({ weatherStation, stationSelections, isMultiStation = true }) => {
  const {
    issuedBy,
    setIssuedBy,
    forecastId,
    setForecastId,
    allWeatherForecasts,
    textDescription,
    issuedByOptions,
    forecaster,
    confidence,
    breakpoints,
  } = useWeatherForecastContext(); // Retrieve values from the weather forecast context
  //the below is used for data table
  const selectedStatus = useSelector(selectStatus);
  const singleDisplayRange = useSelector(selectDataRange);
  const singleEndDate = useSelector(selectEndDate);

  const { status, dataRange, presetId } = isMultiStation
    ? useMultiStationContext()
    : {
        status: selectedStatus,
        dataRange: null,
        forecastWeatherStationId: weatherStation.Id,
        presetId: null,
      };
  const dispatch = useDispatch();

  //based on the date and weather station we want to retrieve a list of issued forecasts
  //isMultiStation:true fro multi-station graph or false for data table
  useEffect(() => {
    //This is only for data table
    if (!weatherStation || isMultiStation) return;
    dispatch(
      fetchWeatherForecasts({
        weatherStationId: weatherStation.Id,
        endDate: singleEndDate?.value,
        startDate: configStartDate(singleEndDate?.value, singleDisplayRange),
        isMultiStation: isMultiStation,
        presetId: presetId,
      }),
    );
  }, [singleEndDate, weatherStation]);

  useEffect(() => {
    //This is only for multi-station graph
    if (!weatherStation || !isMultiStation) return;
    dispatch(
      fetchWeatherForecasts({
        weatherStationId: weatherStation.Id,
        endDate: dataRange.endDate,
        startDate: configStartDate(dataRange.endDate, dataRange.displayRange),
        isMultiStation: isMultiStation,
        presetId: presetId,
      }),
    );
  }, [dataRange, weatherStation]);

  useEffect(() => {
    if (!allWeatherForecasts) return;
    if ((isMultiStation && !dataRange) || (!isMultiStation && !singleEndDate)) return;
    const _dataRange = isMultiStation ? dataRange : { endDate: singleEndDate?.value, displayRange: singleDisplayRange };

    dispatch(
      fetchWeatherForecast({
        isMultiStation: isMultiStation,
        endDate: _dataRange.endDate,
        startDate: configStartDate(_dataRange.endDate, _dataRange.displayRange),
        weatherForecastId: forecastId,
        weatherStationId: weatherStation?.Id,
        presetId: presetId,
      }),
    );
  }, [dataRange, singleEndDate, forecastId, allWeatherForecasts]);

  /**
   * Helper functions for the WeatherForecastComponent
   *
   */
  const getSelectedWeatherForecastOption = () => {
    let selectedOpt = allWeatherForecasts.find((f) => f.Id === forecastId) || allWeatherForecasts[0];
    return selectedOpt ? { value: selectedOpt.Id, label: getUtcToLocal(selectedOpt.ForecastIssueTimestamp) } : null;
  };

  const handleIssuedByChange = (e) => {
    setIssuedBy(e.value);
  };

  const handleIssuedTimeChange = (e) => {
    setForecastId(e.value);
  };

  const moveStationSelectionForward = () => {
    stationSelections.moveWXForward();
  };

  const moveStationSelectionBackward = () => {
    stationSelections.moveWXBackward();
  };
  const isForecast = () => {
    if (isMultiStation)
      return (
        status &&
        status.weatherForecasts !== STATE_STATUS.LOADING &&
        status.weatherForecast !== STATE_STATUS.LOADING &&
        forecastId > 0 &&
        (presetId > 0 || presetId === DEFAULT_PRESET_ID_FOR_ALL_DATASETS) &&
        allWeatherForecasts &&
        allWeatherForecasts.length > 0
      );
    return (
      status &&
      status.weatherForecasts !== STATE_STATUS.LOADING &&
      status.weatherForecast !== STATE_STATUS.LOADING &&
      forecastId > 0 &&
      allWeatherForecasts &&
      allWeatherForecasts.length > 0
    );
  };
  return (
    <>
      {weatherStation && (
        <Accordion defaultActiveKey="1">
          <Card border="light" className={'page-card__fullscreen'}>
            <CardHeaderToggle as="h3" eventKey="1" title={'Weather Station Forecast'} />
            <Accordion.Collapse eventKey="1">
              <Card.Body>
                <Row>
                  <Col xxl={4} xl={4} lg={4} md={'auto'} sm={'auto'} xs={'auto'} xxs={'auto'}>
                    <Row>
                      {isMultiStation && (
                        <Col xxl={1} xl={1} lg={1} md={1} sm={1} xs={1} xxs={1}>
                          <Button
                            onClick={moveStationSelectionForward}
                            className="carousel-button bi bi-caret-left-fill"
                            aria-label="left-scroll"
                          />
                        </Col>
                      )}
                      <Col>
                        <Card style={{ border: '0px', display: 'inline-block' }}>
                          <Card.Body>
                            <Card.Title as="h4">{weatherStation.Name}</Card.Title>
                            {EnabledFeature.WeatherForecastMap && isForecast() && (
                              <>
                                <Card.Subtitle className="mb-2 text-muted small">
                                  {'Elevation: ' + weatherStation.Elevation + 'm'}
                                </Card.Subtitle>
                                <Card.Link style={{ fontSize: '0.75rem' }} href="/Maps">
                                  View on Map
                                </Card.Link>
                              </>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>

                      {isMultiStation && (
                        <Col xxl={1} xl={1} lg={1} md={1} sm={1} xs={1} xxs={1}>
                          <Button
                            onClick={moveStationSelectionBackward}
                            className="carousel-button bi bi-caret-right-fill"
                            aria-label="right-scroll"
                          />
                        </Col>
                      )}
                    </Row>
                  </Col>
                  {!breakpoints.isLarge && (
                    <Col md={5}>
                      <Card style={{ border: '0rem' }}>
                        <Card.Body>
                          <Card.Title as="h4">Forecast</Card.Title>
                          <Row className="dataTables-validation-font">
                            <Col md={12} style={{ zIndex: 6, marginBottom: '0.75rem' }}>
                              <Form.Label as="h5">
                                <b>Forecast Issued By</b>
                              </Form.Label>
                              {issuedBy && (
                                <Select
                                  options={issuedByOptions}
                                  value={issuedByOptions.find((f) => f.value === issuedBy)}
                                  onChange={handleIssuedByChange}
                                />
                              )}
                            </Col>
                          </Row>
                          <Row className="dataTables-validation-font">
                            <Col md={12} style={{ zIndex: 2 }}>
                              <Form.Group controlId="v_issued_by_date_input_controlId">
                                <Form.Label as="h5">
                                  <b>Issue Time &#38; Date</b>
                                </Form.Label>
                                <Select
                                  options={allWeatherForecasts.map((wf) => {
                                    return { value: wf.Id, label: getUtcToLocal(wf.ForecastIssueTimestamp) };
                                  })}
                                  value={getSelectedWeatherForecastOption()}
                                  placeholder={'Select issued time'}
                                  onChange={handleIssuedTimeChange}
                                  maxMenuHeight={200}
                                  isDisabled={issuedBy === '-1'}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                  <Col md={!breakpoints.isLarge ? 12 : !breakpoints.isXLarge ? 4 : 3}>
                    <Card style={{ border: '0px' }}>
                      {isForecast() && (
                        <Card.Body>
                          <Card.Title as="h4">{'Pavement Condition'}</Card.Title>
                          <WeatherForecastLegends type="conidtion" />
                        </Card.Body>
                      )}
                    </Card>
                  </Col>
                  <Col md={!breakpoints.isLarge ? 12 : !breakpoints.isXLarge ? 4 : 3}>
                    <Card style={{ border: '0px' }}>
                      {isForecast() && (
                        <Card.Body>
                          <Card.Title as="h4">{'Pavement Temperature'}</Card.Title>
                          {isForecast() && <WeatherForecastLegends type="temperature" />}
                        </Card.Body>
                      )}
                    </Card>
                  </Col>
                  {breakpoints.isLarge && (
                    <Col md={5}>
                      <Row>
                        <Col>
                          <Card style={{ border: '0rem' }}>
                            <Card.Body>
                              <Card.Title as="h4">Forecast</Card.Title>
                              <Row className="dataTables-validation-font">
                                <Col md={12} style={{ zIndex: 6, marginBottom: '0.75rem' }}>
                                  <Form.Label as="h5">
                                    <b>Forecast Issued By</b>
                                  </Form.Label>
                                  {issuedBy && (
                                    <Select
                                      options={issuedByOptions}
                                      value={issuedByOptions.find((f) => f.value === issuedBy)}
                                      onChange={handleIssuedByChange}
                                    />
                                  )}
                                </Col>
                              </Row>
                              <Row className="dataTables-validation-font">
                                <Col md={12} style={{ zIndex: 2 }}>
                                  <Form.Group controlId="v_issued_by_date_input_controlId">
                                    <Form.Label as="h5">
                                      <b>Issue Time &#38; Date</b>
                                    </Form.Label>
                                    <Select
                                      options={allWeatherForecasts.map((wf) => {
                                        return { value: wf.Id, label: getUtcToLocal(wf.ForecastIssueTimestamp) };
                                      })}
                                      value={getSelectedWeatherForecastOption()}
                                      placeholder={'Select issued time'}
                                      onChange={handleIssuedTimeChange}
                                      maxMenuHeight={200}
                                      isDisabled={issuedBy === '-1'}
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Col>
                  )}
                </Row>

                {status &&
                (status.weatherForecasts === STATE_STATUS.LOADING ||
                  status.weatherForecast === STATE_STATUS.LOADING) ? (
                  <AwpSpinner />
                ) : allWeatherForecasts.length > 0 ? (
                  <>
                    {isForecast() && (
                      <>
                        {isMultiStation ? (
                          <ForecastContainer key={forecastId} isSingleStation={false} />
                        ) : (
                          <ForecastContainer
                            key={forecastId}
                            isSingleStation={true}
                            singleStationId={weatherStation.Id}
                            singleHourlyEnd={textDescription?.HourlyEnd}
                            singleDisplayRange={singleDisplayRange}
                          />
                        )}
                      </>
                    )}
                    {isForecast() && <ForecastTimeDays />}
                  </>
                ) : (
                  <Col md={12} style={{ padding: '12px', border: '1px solid #ffc107', backgroundColor: '#fff3cd' }}>
                    <IconExclamationCircleWarning style={{ paddingBottom: '4px', paddingRight: '4px' }} />
                    {'No forecast available for the selected date/time range'}
                  </Col>
                )}
                <Row style={{ paddingLeft: '0.625rem' }}>
                  <Col md={4} style={{ fontSize: '80%' }}>
                    {!isValueEmpty(textDescription) ? 'Confidence: ' + toStringOrEmpty(confidence) : ''}
                  </Col>
                  <Col md={4} style={{ fontSize: '80%' }}>
                    {!isValueEmpty(textDescription) ? 'Forecaster: ' + toStringOrEmpty(forecaster) : ''}
                  </Col>
                </Row>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      )}
    </>
  );
};

export default WeatherForecast;
